"use strict";

/**
 * SageReportsService
 *
 * Thin wrapper around every `sec/sage-reports/*` API endpoint exposed by the
 * SAGE System Integration backend (WEB-3328). Every endpoint is project-scoped
 * — the caller must supply a `property_id` and the backend derives the
 * developer from the property. No `developer_id` is sent from the UI.
 *
 * Generate endpoints stream the CSV file body directly on success (text/csv
 * + Content-Disposition: attachment) and only return JSON on error, so they
 * bypass SSHttpService and use $http with responseType:'blob' — see
 * `_generateCsv` below.
 *
 * @remarks Used by:
 *   - resources/assets/js/views/developer/sage_reports/invoice_export.js
 *   - resources/assets/js/views/developer/sage_reports/receipt_export.js
 *   - resources/assets/js/views/developer/sage_reports/history.js
 *   - (iter-2/3) unlock.js, audit_log.js, code_mappings.js
 */
class SageReportsService {

    constructor($http, $q, SSHttpService, SSUtilService) {
        const self = this;
        self.$http = $http;
        self.$q = $q;
        self.SSHttpService = SSHttpService;
        self.SSUtilService = SSUtilService;
    }

    // -------------------------------------------------------------------------
    // Invoice Export
    // -------------------------------------------------------------------------

    /**
     * Generate an invoice export report for a project + date range.
     *
     * @param {Object} payload — { property_id, date_from, date_to }
     * @returns Promise resolving to either:
     *   - `{ blob: Blob, filename: string }` on HTTP 200 (the CSV body to save), or
     *   - an Error with `.code`, `.message`, `.data` on failure (e.g. 409).
     */
    generateInvoices(payload) {
        return this._generateCsv('sec/sage-reports/invoices/generate', payload);
    }

    /**
     * Fetch the invoice-export history rows for a project (Laravel paginator).
     * @param {Object} params — { property_id, status?, version?, from?, to?, per_page?, page? }
     * @returns Response payload `{ success, data: { current_page, data: [rows], total, ... } }`.
     */
    getInvoiceHistory(params) {
        return this._getPaginator('sec/sage-reports/invoices/history', params);
    }

    /**
     * Backend endpoint that returns a fresh presigned URL for an existing
     * history row. The `url` / `s3_file_url` embedded in the listing rows
     * is expired (or otherwise not directly fetchable), so call this for
     * every download instead of opening the row's URL directly.
     */
    getInvoiceDownloadUrl(historyId, propertyId) {
        return this._getJSON('sec/sage-reports/invoices/history/' + historyId + '/download', {
            property_id: propertyId
        });
    }

    // -------------------------------------------------------------------------
    // Receipt Export
    // -------------------------------------------------------------------------

    generateReceipts(payload) {
        return this._generateCsv('sec/sage-reports/receipts/generate', payload);
    }

    getReceiptHistory(params) {
        return this._getPaginator('sec/sage-reports/receipts/history', params);
    }

    getReceiptDownloadUrl(historyId, propertyId) {
        return this._getJSON('sec/sage-reports/receipts/history/' + historyId + '/download', {
            property_id: propertyId
        });
    }

    // -------------------------------------------------------------------------
    // Admin override actions
    // -------------------------------------------------------------------------

    unlock(payload) {
        return this.SSHttpService.postAPIRequest('sec/sage-reports/admin/unlock', payload);
    }

    /**
     * Regenerate an existing history row. The server returns the
     * freshly-built CSV body directly on success (text/csv +
     * Content-Disposition: attachment), and a JSON error only on failure
     * — same shape as the generate endpoints — so the call goes through
     * the shared _generateCsv helper instead of SSHttpService.
     */
    regenerate(historyId) {
        return this._generateCsv('sec/sage-reports/admin/regenerate/' + historyId, {});
    }

    getAuditLog(params) {
        return this.SSHttpService.getAPIRequest('sec/sage-reports/admin/audit-log', params);
    }

    // -------------------------------------------------------------------------
    // Payment-code mappings CRUD
    // -------------------------------------------------------------------------

    listCodeMappings(params) {
        return this.SSHttpService.getAPIRequest('sec/sage-reports/admin/code-mappings', params);
    }

    createCodeMapping(payload) {
        return this.SSHttpService.postAPIRequest('sec/sage-reports/admin/code-mappings', payload);
    }

    updateCodeMapping(id, payload) {
        return this.SSHttpService.putAPIRequest('sec/sage-reports/admin/code-mappings/' + id, payload);
    }

    deleteCodeMapping(id, propertyId) {
        return this.SSHttpService.deleteAPIRequest('sec/sage-reports/admin/code-mappings/' + id, {
            property_id: propertyId
        });
    }

    // -------------------------------------------------------------------------
    // CSV download helpers
    // -------------------------------------------------------------------------

    /**
     * Trigger a browser-native "save as" for an in-memory Blob. Uses
     * URL.createObjectURL + a hidden anchor — no external dependency.
     */
    triggerCsvDownload(blob, filename) {
        if (!blob) { return; }
        let objectUrl = URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename || 'sage-report.csv';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(function () { URL.revokeObjectURL(objectUrl); }, 0);
    }

    /**
     * GET a Laravel paginator endpoint and normalise the response so callers
     * can read `response.data.data` (rows) / `response.data.total` (count)
     * regardless of whether the backend wraps the paginator in the standard
     * `{success: true, data: <paginator>}` envelope or returns the paginator
     * directly at the top level.
     *
     * Bypasses SSHttpService because that wrapper rejects responses whose
     * body has no `success` field — and the SAGE history endpoints currently
     * return raw Laravel paginators without that wrapper.
     */
    _getPaginator(endpoint, params) {
        const self = this;
        let fullUrl = self.SSHttpService.getApiURL(endpoint);
        let qs = Object.assign({}, params || {}, {
            device_uuid: self.SSUtilService.localStorageUUID()
        });

        return self.$http.get(fullUrl, {
            params: qs,
            withCredentials: true
        }).then(function (resp) {
            let body = resp.data || {};

            // Shape 1 — already standard wrapped envelope (has `success`):
            // return body verbatim. `response.data` will be the paginator
            // payload.
            if (body.success !== undefined) {
                return body;
            }

            // Shape 2 — iter-17 envelope (no `success`, but `data` already
            // wraps the resource + pagination):
            //   { data: { invoice_history: [...], pagination: {...} } }
            // Return body verbatim — `response.data` resolves to the resource
            // envelope so the call-site can read response.data.invoice_history
            // / .receipt_history / .pagination. Without this branch the next
            // `else` would re-wrap (`response.data` would become the *outer*
            // body), and `response.data.invoice_history` would be undefined —
            // the call-site's defensive fallback to `response.data.data` would
            // then return an *object* (the iter-17 inner payload), which
            // ng-repeat iterates field-by-field and renders as ghost rows.
            if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)
                && (body.data.pagination
                    || body.data.invoice_history
                    || body.data.receipt_history
                    || body.data.audit_log)) {
                return body;
            }

            // Shape 3 — pre-iter-17 raw Laravel paginator at the top
            // ({current_page, data: [rows], total, ...}). Wrap so the
            // call-site reads `response.data.data` for rows and
            // `response.data.{current_page,total,...}` for pagination.
            return { success: true, data: body };
        }, function (resp) {
            return self._parseJSONError(resp);
        });
    }

    /**
     * GET a JSON endpoint that may or may not use the standard
     * `{success, data}` envelope. Bypasses SSHttpService so unwrapped
     * responses (e.g. the SAGE history + download endpoints) don't get
     * rejected by the envelope check. The result is always normalised to
     *   { success: true, data: <body> }
     * so callers can read `response.data.<field>` uniformly.
     */
    _getJSON(endpoint, params) {
        const self = this;
        let fullUrl = self.SSHttpService.getApiURL(endpoint);
        let qs = Object.assign({}, params || {}, {
            device_uuid: self.SSUtilService.localStorageUUID()
        });

        return self.$http.get(fullUrl, {
            params: qs,
            withCredentials: true
        }).then(function (resp) {
            let body = resp.data || {};
            if (body.success !== undefined) {
                return body; // wrapped: response.data === payload
            }
            return { success: true, data: body }; // unwrapped → wrap
        }, function (resp) {
            return self._parseJSONError(resp);
        });
    }

    /**
     * Synthesise an Error object from a failed $http JSON response so the
     * shape matches what SSHttpService returns for failures (call-sites can
     * branch on `result instanceof Error` and `result.code` consistently).
     */
    _parseJSONError(resp) {
        let body = (resp && resp.data) || {};
        let errorObj = new Error(body.message || 'Request failed');
        errorObj.code = (body.code !== undefined) ? body.code : ((resp && resp.status) || 0);
        if (body.sub_code !== undefined) { errorObj.sub_code = body.sub_code; }
        if (body.data !== undefined) { errorObj.data = body.data; }
        return errorObj;
    }

    /**
     * POST to a generate endpoint expecting a CSV body on success (200) and
     * a JSON error body on failure (409 etc). Bypasses SSHttpService because
     * that wrapper assumes JSON responses.
     */
    _generateCsv(endpoint, payload) {
        const self = this;
        let fullUrl = self.SSHttpService.getApiURL(endpoint);
        let body = Object.assign({}, payload || {}, {
            device_uuid: self.SSUtilService.localStorageUUID()
        });

        return self.$http.post(fullUrl, body, {
            withCredentials: true,
            responseType: 'blob'
        }).then(function (resp) {
            return {
                blob: resp.data,
                filename: self._filenameFromHeaders(resp.headers) || self._fallbackFilename(endpoint)
            };
        }, function (resp) {
            return self._parseBlobError(resp);
        });
    }

    _filenameFromHeaders(headersFn) {
        try {
            let cd = (headersFn && (headersFn('Content-Disposition') || headersFn('content-disposition'))) || '';
            let m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
            if (m && m[1]) { return decodeURIComponent(m[1].replace(/"/g, '')); }
        } catch (e) {}
        return null;
    }

    _fallbackFilename(endpoint) {
        let stamp = new Date().toISOString().replace(/[^0-9]/g, '').slice(0, 14);
        let type = endpoint.indexOf('receipts') > -1 ? 'receipt' : 'invoice';
        return 'sage_' + type + '_' + stamp + '.csv';
    }

    /**
     * The error response is a Blob (because we requested responseType:'blob');
     * read it as text, JSON.parse, and synthesise the same shape SSHttpService
     * returns for failures so call-sites can handle both flows identically
     * (e.g. checking `result.code === 409`).
     */
    _parseBlobError(resp) {
        const self = this;
        let deferred = self.$q.defer();
        let status = (resp && resp.status) || 0;

        let resolveWith = function (body) {
            let errorObj = new Error((body && body.message) || 'Request failed');
            errorObj.code = (body && body.code !== undefined) ? body.code : status;
            if (body && body.sub_code !== undefined) { errorObj.sub_code = body.sub_code; }
            if (body && body.data !== undefined) { errorObj.data = body.data; }
            deferred.resolve(errorObj);
        };

        if (!resp || !resp.data) {
            resolveWith({ message: 'Network error' });
            return deferred.promise;
        }

        if (typeof Blob !== 'undefined' && resp.data instanceof Blob) {
            let reader = new FileReader();
            reader.onload = function () {
                try { resolveWith(JSON.parse(reader.result || '{}')); }
                catch (e) { resolveWith({ message: 'Server returned an unparseable error response' }); }
            };
            reader.onerror = function () {
                resolveWith({ message: 'Failed to read error response' });
            };
            reader.readAsText(resp.data);
        } else {
            resolveWith(resp.data || {});
        }

        return deferred.promise;
    }
}

SageReportsService.$inject = ['$http', '$q', 'SSHttpService', 'SSUtilService'];
app.service('SageReportsService', SageReportsService);
